import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CalendarMinus } from "lucide-react";
import { useAppDispatch } from "../../app/hooks";
import Button from "../button/Button";
import ModalShell from "../modal/ModalShell";
import { CancellationPreviewResponse } from "../../features/portal/contracts/CancellationPreviewResponse";
import {
  cancelPortalOccurrence,
  previewOccurrenceCancellation,
} from "../../features/portal/slice-extensions/portal-cancellation-slice-extension";
import { cancellationConfirmText } from "../../features/portal/utils/policySentence";

interface PortalCancelOccurrenceDialogProps {
  memberId: string;
  bookingId: string;
  date: string; // YYYY-MM-DD
  title: string;
  dateLabel: string;
  onClose: () => void;
  /** Called with the applied outcome after a successful cancellation. */
  onCancelled: (result: CancellationPreviewResponse) => void;
}

/**
 * Shows what cancelling one occurrence costs under the Session's cancellation policy —
 * free, or late with the fee — and cancels on confirmation.
 */
const PortalCancelOccurrenceDialog = ({
  memberId,
  bookingId,
  date,
  title,
  dateLabel,
  onClose,
  onCancelled,
}: PortalCancelOccurrenceDialogProps) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const [preview, setPreview] = useState<CancellationPreviewResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    dispatch(previewOccurrenceCancellation({ memberId, bookingId, date }))
      .unwrap()
      .then(setPreview)
      .catch((rejected: { message?: string }) => setError(rejected?.message ?? t("portal.cancel.failed")));
  }, [dispatch, memberId, bookingId, date, t]);

  const handleConfirm = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await dispatch(cancelPortalOccurrence({ memberId, bookingId, date })).unwrap();
      onCancelled(result);
    } catch (rejected) {
      setError((rejected as { message?: string })?.message ?? t("portal.cancel.failed"));
      setBusy(false);
    }
  };

  return (
    <ModalShell onClose={onClose} labelledBy="portal-cancel-title" describedBy="portal-cancel-description">
      <div className="p-6">
        <h3 id="portal-cancel-title" className="flex items-center gap-2 text-lg font-semibold text-grey-10 dark:text-grey-90">
          <CalendarMinus size={20} aria-hidden="true" />
          {t("portal.cancel.title")}
        </h3>
        <p className="mt-1 text-sm text-grey-40 dark:text-grey-80">
          {title} · {dateLabel}
        </p>
        <div id="portal-cancel-description" className="mt-4 text-grey-30 dark:text-grey-70">
          {preview ? (
            <>
              <p>{cancellationConfirmText(preview, t, i18n.language)}</p>
              {preview.note && <p className="mt-2 text-sm text-grey-40 dark:text-grey-80">{preview.note}</p>}
            </>
          ) : (
            !error && <p>{t("common.loading", "Loading…")}</p>
          )}
        </div>
        {error && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>}
        <div className="mt-6 flex items-center justify-end gap-3">
          <Button type="button" variant="secondary" onClick={onClose}>
            {t("portal.cancel.keep")}
          </Button>
          <Button type="button" variant="danger" onClick={handleConfirm} disabled={!preview || busy}>
            {busy ? t("common.loading", "Loading…") : t("portal.cancel.confirmButton")}
          </Button>
        </div>
      </div>
    </ModalShell>
  );
};

export default PortalCancelOccurrenceDialog;
